import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { push } from 'redux-first-history';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { history } from 'app-redux';
import constants from 'ga/constants';
import { notifyEvent } from 'utils/analytics';
import { Geoname } from 'utils/constants';
import { extractTimeframeStartDate } from 'utils/routerUtils';
import { FuncReturnTypeSync } from 'utils/typeHelpers';

import {
  updateAoiLayer,
  updateEventsLayerVisibility,
  updateOutbreakAndAirportLayer,
} from 'map/map.saga';

import { fetchGlobalDiseaseRisks } from 'diseaseActivitiesGlobal/diseaseActivitiesGlobal.saga';
import { resetFilters as resetFiltersDiseasesGlobal } from 'diseaseActivitiesGlobal/diseaseActivitiesGlobal.slice';
import {
  fetchCalculationBreakdown,
  fetchDiseaseActivityDetails,
  fetchDiseaseActivityList,
} from 'diseaseActivitiesInCountryLocations/diseaseActivitiesInCountryLocations.saga';
import { resetDiseaseActivityListFilters } from 'diseaseActivitiesInCountryLocations/diseaseActivitiesInCountryLocations.slice';
import {
  fetchDiseaseActivityUpdates,
  fetchTopDescriptionForCountry,
  fetchTopDescriptionForUserLocation,
} from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.saga';
import diseaseActivityUpdatesSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.selectors';
import { fetchDiseaseRisks } from 'diseaseActivitiesInLocation/diseaseActivitiesInLocation.saga';
import { resetFilters as resetFiltersDiseasesInLocation } from 'diseaseActivitiesInLocation/diseaseActivitiesInLocation.slice';
import {
  DiseaseActivityParams,
  DiseaseActivityParamsPayload,
  PanelType,
  setParameters,
  updatePanels,
} from 'diseaseActivityPanels/diseaseActivityPanels.slice';
import { fetchLocations } from 'locations/locations.saga';

import {
  DiseaseActivityPanelReloadFlags,
  mapDiseaseActivityParamsToPanelState,
} from './disacPanelUtils';
import diseaseActivityPanelsSelectors from './diseaseActivityPanels.selectors';

type OpenPanelActionPayload = {
  triggerAnalytics?: boolean;
  title?: string;
} & (
  | {
      panel: 'Diseases';
      geonameId: number;
    }
  | {
      panel: 'DiseaseActivityList';
      diseaseId: number;
      speciesId?: number;
    }
  | {
      panel: 'DiseaseActivityDetails';
      countryId: number;
    }
  | {
      panel: 'DiseaseActivityUpdatesFromList';
      activityDate?: string;
    }
  | {
      panel: 'DiseaseActivityUpdatesFromDetails';
      activityDate?: string;
    }
  | {
      panel: 'RiskParameters';
    }
);

export const setDiseaseActivityParams = createAction<DiseaseActivityParamsPayload>(
  'panels/setDiseaseActivityParams'
);
export const openPanel = createAction<OpenPanelActionPayload>('panels/openPanel');
export const closePanel = createAction<PanelType>('panels/closePanel');

function* navigateWithSearchParams(to: string) {
  const [date, validDate] = extractTimeframeStartDate(history.location.search);
  if (!window?.location?.search?.length || !validDate) yield put(push(to));
  else yield put(push(`${to}?startDate=${date}`));
}

function setDiseaseActivityParamsSaga() {
  return function* (action: PayloadAction<DiseaseActivityParamsPayload>) {
    const disacParamsNew = action.payload;
    const {
      activePanel,
      isVisibleDLP,
      isVisibleDELP,
      isVisibleEDP,
      isVisibleTRANSPAR,
      isVisibleHistory,
    } = mapDiseaseActivityParamsToPanelState(disacParamsNew);
    const {
      needToReloadLocations,
      needToReloadDiseases,
      needToReloadDiseaseActivityList,
      needToReloadDiseaseActivityDetails,
      needToReloadDiseaseActivityUpdates,
      needtoReloadRiskParameters,
    }: DiseaseActivityPanelReloadFlags = yield select(
      diseaseActivityPanelsSelectors.whatPanelsNeedToReload(disacParamsNew)
    );

    yield put(setParameters(disacParamsNew));
    yield put(
      updatePanels({
        open: [
          PanelType.MyLocationsPanel,
          ...(isVisibleDLP ? [PanelType.DiseasesPanel] : []),
          ...(isVisibleDELP ? [PanelType.DiseaseActivityListPanel] : []),
          ...(isVisibleEDP ? [PanelType.DiseaseActivityDetailsPanel] : []),
          ...(isVisibleHistory ? [PanelType.DiseaseActivityUpdatesPanel] : []),
          ...(isVisibleTRANSPAR ? [PanelType.ParametersPanel] : []),
        ],
        activePanel: activePanel,
      })
    );

    if (needToReloadLocations) {
      yield put(fetchLocations());
    }
    if (needToReloadDiseases) {
      if (disacParamsNew.userLocationId === Geoname.GLOBAL_VIEW) {
        yield put(fetchGlobalDiseaseRisks());
        yield put(resetFiltersDiseasesGlobal());
      } else {
        yield put(fetchDiseaseRisks());
        yield put(resetFiltersDiseasesInLocation());
      }
    }
    if (needToReloadDiseaseActivityList) {
      yield put(fetchDiseaseActivityList());
      yield put(fetchTopDescriptionForUserLocation());
      yield put(resetDiseaseActivityListFilters());
    }
    if (needToReloadDiseaseActivityDetails) {
      yield put(fetchDiseaseActivityDetails());
      yield put(fetchTopDescriptionForCountry());
    }
    if (needToReloadDiseaseActivityUpdates) {
      const filter: FuncReturnTypeSync<typeof diseaseActivityUpdatesSelectors.filter> =
        yield select(diseaseActivityUpdatesSelectors.filter);
      yield put(
        fetchDiseaseActivityUpdates({
          excludeCaseCounts: filter === 'with-updates-only',
        })
      );
    }
    if (needtoReloadRiskParameters) {
      yield put(fetchCalculationBreakdown());
    }

    yield put(updateAoiLayer());
    yield put(updateEventsLayerVisibility());
    yield put(updateOutbreakAndAirportLayer());
  };
}

function openPanelSaga() {
  return function* (action: PayloadAction<OpenPanelActionPayload>) {
    const target = action.payload;
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId } = disacParams;
    // prettier-ignore
    const goto =
      target.panel === 'Diseases'
        ? `/location/${target.geonameId}`
        : target.panel === 'DiseaseActivityList'
        ? `/location/${userLocationId}/disease/${target.diseaseId}/species/${target.speciesId || 1}`
        : target.panel === 'DiseaseActivityDetails'
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId || 1}/country/${target.countryId}`
        : target.panel === 'DiseaseActivityUpdatesFromList'
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId || 1}/activity/${target.activityDate ?? ''}`
        : target.panel === 'DiseaseActivityUpdatesFromDetails'
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId || 1}/country/${countryId}/activity/${target.activityDate ?? ''}`
        : target.panel === 'RiskParameters'
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId || 1}/country/${countryId}/parameters`
        : null;
    if (goto) yield navigateWithSearchParams(goto);

    if (target.triggerAnalytics) {
      const analyticsPayload =
        target.panel === 'DiseaseActivityDetails'
          ? {
              action: constants.Action.OPEN_EVENT_DETAILS,
              category: constants.Category.EVENTS,
              label: `Open from list: disease/${diseaseId}/species/${speciesId}/country/${countryId} | ${target.title}`,
              value: `${diseaseId}-${speciesId}-${target.countryId}`,
            }
          : null;

      if (analyticsPayload) yield call(notifyEvent, analyticsPayload);
    }
  };
}

function closePanelSaga() {
  return function* (action: PayloadAction<PanelType>) {
    const closingPanel = action.payload;
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId } = disacParams;
    const goto =
      closingPanel === PanelType.ParametersPanel ||
      (closingPanel === PanelType.DiseaseActivityUpdatesPanel && countryId)
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId}/country/${countryId}`
        : closingPanel === PanelType.DiseaseActivityDetailsPanel ||
          (closingPanel === PanelType.DiseaseActivityUpdatesPanel && !countryId)
        ? `/location/${userLocationId}/disease/${diseaseId}/species/${speciesId}`
        : closingPanel === PanelType.DiseaseActivityListPanel
        ? `/location/${userLocationId}`
        : closingPanel === PanelType.DiseasesPanel
        ? `/location`
        : null;
    if (goto) yield navigateWithSearchParams(goto);
  };
}

function* diseaseActivityPanelsSaga() {
  yield takeLatest(setDiseaseActivityParams, setDiseaseActivityParamsSaga());
  yield takeLatest(openPanel, openPanelSaga());
  yield takeLatest(closePanel, closePanelSaga());
}

export default diseaseActivityPanelsSaga;
